import {
	Cartesian2,
	Cartesian3,
	Color,
	LabelStyle,
	NearFarScalar,
	VerticalOrigin,
	HeightReference,
	CallbackProperty,
	PolygonHierarchy,
	defined,
	Ellipsoid,
	PolylineDashMaterialProperty,
	BoundingSphere,
	Math as CesiumMath,
} from 'cesium';

import Subscriber from './subscriber';
import { calculatePolygonArea } from '@/helper/cesiumHelper';
import { getLanguage, t } from '@/i18n';
import { numberLocaleFor } from '@/i18n/format';

export default class MeasurementDrawer {
	constructor(viewer, options = {}) {
		if (!viewer) throw new Error('No Cesium viewer provided');

		this._viewer = viewer;
		this._callBack = options.callback;
		this._posChange = options.posChange;
		this._options = Object.assign(
			{
				accuracy: 6,
				once: true,
				retainGeo: true,
			},
			options
		);

		this._mode = 'polyline'; // 'polyline' | 'polygon' | 'height'
		this._positions = Array.isArray(options.positions) ? options.positions : [];
		const defaultTips = {
			leftClick: t('viewer.measure.leftClick'),
			rightClick: t('viewer.measure.rightClick'),
			doubleClick: t('viewer.measure.doubleClick'),
			edit: t('viewer.measure.edit'),
		};
		this._tips = Object.assign(defaultTips, options.tips);

		this._pointGeometry = [];
		this._activePoint = null;
		this._shape = null;
		this._status = 'INIT';
		this._mouseDelta = 10;
		this._lastClickPosition = new Cartesian2(Infinity, Infinity);
		this._hoveredPoint = null;

		this._measurementLabels = []; // Stores dynamically reused measurement entities
		this._heightEntities = [];

		if (!this._viewer.scene.pickPositionSupported) {
			console.warn(
				'Browser does not support pickPosition; terrain-based point selection may be incorrect.'
			);
		}

		// SAFER: Use Entity for the tip label instead of Primitive LabelCollection
		// Prevents "throwOnDestroyed" crash during React unmounts
		this._cursorLabelEntity = this._viewer.entities.add({
			label: {
				text: 'start',
				font: 'bold 1rem Arial',
				fillColor: Color.WHITE,
				backgroundColor: Color.fromCssColorString('#000000'),
				showBackground: true,
				backgroundPadding: new Cartesian2(4, 4),
				outlineWidth: 4,
				style: LabelStyle.FILL_AND_OUTLINE,
				pixelOffset: new Cartesian2(4, 30),
				scaleByDistance: new NearFarScalar(1, 0.85, 8e6, 0.75),
				disableDepthTestDistance: Number.POSITIVE_INFINITY,
				show: false,
			},
		});

		this._subscriber = new Subscriber(this._viewer);
		this._events = [];
	}

	get status() {
		return this._status;
	}

	get isDestroy() {
		return this._status === 'DESTROY';
	}

	applyCameraOffset(position) {
		if (!position) return position;
		const cameraPosition = this._viewer.camera.position;
		const direction = Cartesian3.subtract(cameraPosition, position, new Cartesian3());
		Cartesian3.normalize(direction, direction);
		const offset = Cartesian3.multiplyByScalar(direction, 0.15, new Cartesian3());
		return Cartesian3.add(position, offset, new Cartesian3());
	}

	updateLabel(options) {
		if (!this._cursorLabelEntity) return;

		if (!options) {
			if (!this._activePoint) {
				this._cursorLabelEntity.label.show = false;
				return;
			}
			this._cursorLabelEntity.label.show = true;
			const { leftClick, rightClick, doubleClick } = this._tips;
			let tip = `${leftClick}, ${rightClick}`;
			if (this._positions.length > 2) {
				tip = `${leftClick}, ${rightClick}, ${doubleClick}`;
			}
			this._cursorLabelEntity.label.text = tip;
			return;
		}

		const { position, text, show = true } = options;
		if (position) this._cursorLabelEntity.position = position;
		if (text !== undefined) this._cursorLabelEntity.label.text = text;
		this._cursorLabelEntity.label.show = show;
	}

	handlePosChange = () => {
		if (this._posChange) {
			const pos = [];
			const activeIndex = this._activePoint ? this._pointGeometry.indexOf(this._activePoint) : -1;
			this._positions.forEach((cartesian, idx) => {
				if (idx !== activeIndex) pos.push(this.cartesian2lonlat(cartesian));
			});
			this._posChange(pos);
		}
	};

	createCircleImage(color = '#87ceeb', size = 16, outlineColor = '#ffff00', outlineWidth = 2) {
		const canvas = document.createElement('canvas');
		canvas.width = size;
		canvas.height = size;
		const ctx = canvas.getContext('2d');
		const radius = size / 2;
		ctx.beginPath();
		ctx.arc(radius, radius, radius - outlineWidth / 2, 0, Math.PI * 2);
		ctx.fillStyle = outlineColor;
		ctx.fill();
		ctx.beginPath();
		ctx.arc(radius, radius, radius - outlineWidth - 1, 0, Math.PI * 2);
		ctx.fillStyle = color;
		ctx.fill();
		return canvas.toDataURL();
	}

	drawPoint(position) {
		const image = this.createCircleImage();
		const ent = this._viewer.entities.add({
			position,
			billboard: {
				image,
				verticalOrigin: VerticalOrigin.CENTER,
				heightReference: HeightReference.NONE,
				disableDepthTestDistance: Number.POSITIVE_INFINITY,
			},
		});
		this._viewer.scene.requestRender();
		return ent;
	}

	updatePointSize(entity, size) {
		if (entity && entity.billboard) {
			entity.billboard.scale = size / 6;
		}
	}

	onMouseClickPoint = (_, entity) => {
		if (this._status === 'EDITING' && this._activePoint) return;
		const idx = this._pointGeometry.indexOf(entity);
		this.activeOnePosition(idx);
		this._status = 'EDITING';
	};

	// Initialize the shape geometry safely ONLY ONCE avoiding "destroyed / recreation" crashes
	drawShape = () => {
		if (this._shape) return this._shape;

		const id = `new-measurement-${Date.now()}`;
		if (this._mode === 'polygon') {
			this._shape = this._viewer.entities.add({
				id,
				polygon: {
					show: new CallbackProperty(() => this._positions.length >= 3, false),
					hierarchy: new CallbackProperty(() => {
						return this._positions.length >= 3
							? new PolygonHierarchy([...this._positions])
							: undefined;
					}, false),
					material: Color.YELLOW.withAlpha(0.3),
					perPositionHeight: true,
				},
				polyline: {
					show: new CallbackProperty(() => this._positions.length >= 2, false),
					positions: new CallbackProperty(() => {
						return this._positions.length >= 2
							? [...this._positions, this._positions[0]]
							: undefined;
					}, false),
					width: 2,
					material: Color.CYAN,
					depthFailMaterial: Color.CYAN,
					disableDepthTestDistance: Number.POSITIVE_INFINITY,
				},
			});
		} else {
			this._shape = this._viewer.entities.add({
				id,
				polyline: {
					show: new CallbackProperty(() => this._positions.length >= 2, false),
					positions: new CallbackProperty(() => {
						return this._positions.length >= 2 ? [...this._positions] : undefined;
					}, false),
					width: 2,
					material: Color.CYAN,
					depthFailMaterial: Color.CYAN,
					disableDepthTestDistance: Number.POSITIVE_INFINITY,
				},
			});
		}
		this._viewer.scene.requestRender();
		return this._shape;
	};

	handleMouseMove = (movement) => {
		if (!movement.endPosition) return;
		const scene = this._viewer.scene;
		let newPos =
			scene.pickPosition(movement.endPosition) ||
			this._viewer.camera.pickEllipsoid(movement.endPosition, scene.globe.ellipsoid);
		if (newPos) newPos = this.applyCameraOffset(newPos);

		if (this._status === 'END') {
			const ent = this._viewer.scene.pick(movement.endPosition)?.id;
			if (this._hoveredPoint && this._hoveredPoint !== ent) {
				this.updatePointSize(this._hoveredPoint, 5);
				this._hoveredPoint = null;
				this.updateLabel({ show: false });
				this._viewer.canvas.style.cursor = 'default';
			}
			if (!this._hoveredPoint && this._pointGeometry.includes(ent)) {
				this._hoveredPoint = ent;
				this.updatePointSize(ent, 8);
				this.updateLabel({ position: newPos, text: this._tips.edit, show: true });
				this._viewer.canvas.style.cursor = 'pointer';
			}
			return;
		}

		if (!this._activePoint || !newPos) return;
		this._activePoint.position.setValue(newPos);

		const idx = this._pointGeometry.indexOf(this._activePoint);
		if (idx !== -1 && this._positions.length > 0) {
			this._positions.splice(idx, 1, newPos);
		}

		this.updateLabel({ position: newPos });
		this.updateMeasurements();
		this._viewer.scene.requestRender();
	};

	handleMouseLeftClick = (movement) => {
		if (!movement.position || (!this._activePoint && this._mode !== 'height')) return;

		let cart =
			this._viewer.scene.pickPosition(movement.position) ||
			this._viewer.camera.pickEllipsoid(movement.position, this._viewer.scene.globe.ellipsoid);
		if (!cart || !defined(cart)) return;
		cart = this.applyCameraOffset(cart);

		if (this._mode === 'height') {
			this._drawHeightLine(cart);
			return;
		}

		if (
			Cartesian2.magnitude(
				Cartesian2.subtract(this._lastClickPosition, movement.position, new Cartesian2())
			) < this._mouseDelta
		)
			return;

		const idx = this._pointGeometry.indexOf(this._activePoint);
		const pGeo = this.drawPoint(cart);
		this.updatePointSize(this._activePoint, 5);
		this._activePoint = pGeo;

		this._pointGeometry.splice(idx + 1, 0, pGeo);
		this._positions.splice(idx + 1, 0, cart);
		Cartesian2.clone(movement.position, this._lastClickPosition);

		if (this._positions.length === 1) {
			this._positions.push(cart);
			this.drawShape(); // Called once to initialize the shape
		}

		this.updateLabel();
		this.updateMeasurements();
		this.handlePosChange();
	};

	// -------------------------------------------------------------
	// SAFER POOLED LABEL MEASUREMENTS
	// -------------------------------------------------------------

	_drawHeightLine(pos) {
		const carto = Ellipsoid.WGS84.cartesianToCartographic(pos);
		const terrainH = this._viewer.scene.globe.getHeight(carto) || 0;
		const ground = Cartesian3.fromRadians(carto.longitude, carto.latitude, terrainH);
		const diff = carto.height - terrainH;

		const pt1 = this.drawPoint(pos);
		const pt2 = this.drawPoint(ground);
		this._heightEntities.push(pt1, pt2);

		const line = this._viewer.entities.add({
			polyline: {
				positions: [ground, pos],
				width: 2,
				material: new PolylineDashMaterialProperty({ color: Color.YELLOW }),
				disableDepthTestDistance: Number.POSITIVE_INFINITY,
			},
		});
		this._heightEntities.push(line);

		const mid = Cartesian3.midpoint(ground, pos, new Cartesian3());
		const formattedDiff = diff.toLocaleString(numberLocaleFor(getLanguage()), {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		});
		const label = this._viewer.entities.add({
			position: mid,
			label: this._createMeasurementLabel(
				t('viewer.measure.terrainHeight', { value: formattedDiff }),
				true
			),
		});
		this._heightEntities.push(label);
	}

	_createMeasurementLabel(text, isMain = false) {
		return {
			text,
			font: isMain ? 'bold 15px sans-serif' : '13px sans-serif',
			fillColor: Color.WHITE,
			backgroundColor: Color.BLACK.withAlpha(0.6),
			showBackground: true,
			backgroundPadding: new Cartesian2(5, 3),
			disableDepthTestDistance: Number.POSITIVE_INFINITY,
		};
	}

	// This safely caches, cleans up, and reuses labels preventing batching updates from choking
	updateMeasurements() {
		const pts = this._positions;
		if (pts.length < 2) {
			this._measurementLabels.forEach((l) => this._viewer.entities.remove(l));
			this._measurementLabels = [];
			return;
		}

		const neededLabels =
			this._mode === 'polygon' && pts.length >= 3 ? pts.length + 1 : pts.length - 1;

		while (this._measurementLabels.length > neededLabels) {
			const l = this._measurementLabels.pop();
			this._viewer.entities.remove(l);
		}

		while (this._measurementLabels.length < neededLabels) {
			this._measurementLabels.push(
				this._viewer.entities.add({
					position: new Cartesian3(),
					label: this._createMeasurementLabel(''),
				})
			);
		}

		let labelIndex = 0;

		for (let i = 1; i < pts.length; i++) {
			const a = pts[i - 1],
				b = pts[i];
			const dist = Cartesian3.distance(a, b);
			const mid = Cartesian3.midpoint(a, b, new Cartesian3());

			const labelEnt = this._measurementLabels[labelIndex++];
			labelEnt.position.setValue(mid);
			labelEnt.label.text = t('viewer.measure.distance', {
				value: dist.toLocaleString(numberLocaleFor(getLanguage()), { maximumFractionDigits: 2 }),
			});
			labelEnt.label.font = '13px sans-serif';
		}

		if (this._mode === 'polygon' && pts.length >= 3) {
			const first = pts[0],
				last = pts[pts.length - 1];
			const dist = Cartesian3.distance(last, first);
			const mid = Cartesian3.midpoint(last, first, new Cartesian3());

			const closeLabel = this._measurementLabels[labelIndex++];
			closeLabel.position.setValue(mid);
			closeLabel.label.text = t('viewer.measure.distance', {
				value: dist.toLocaleString(numberLocaleFor(getLanguage()), { maximumFractionDigits: 2 }),
			});
			closeLabel.label.font = '13px sans-serif';

			const area = calculatePolygonArea(pts, { unit: 'm2', formatted: false });
			const center = BoundingSphere.fromPoints(pts).center;
			const areaLocale = numberLocaleFor(getLanguage());
			const formatArea =
				area > 1000000
					? `${(area / 1000000).toLocaleString(areaLocale, { maximumFractionDigits: 2 })} km²`
					: `${area.toLocaleString(areaLocale, { maximumFractionDigits: 2 })} m²`;

			const areaLabel = this._measurementLabels[labelIndex++];
			areaLabel.position.setValue(center);
			areaLabel.label.text = t('viewer.measure.area', { value: formatArea });
			areaLabel.label.font = 'bold 15px sans-serif';
		}
	}

	// -------------------------------------------------------------
	// STANDARD POLYGONDRAWER REPLICATION
	// -------------------------------------------------------------

	onClickToActivateEditMode = (_, entity) => {
		const idx = this._pointGeometry.indexOf(entity);
		if (idx === -1) return;

		this._status = 'EDITING';
		this._subscriber.remove(this._pointGeometry, 'LEFT_CLICK');
		this._subscriber.removeNative(this._viewer, 'LEFT_DOUBLE_CLICK');
		this._subscriber.add(this._pointGeometry, this.onMouseClickPoint, 'LEFT_CLICK');

		if (this._events.length === 0) {
			this._events = [
				this._subscriber.addExternal(this.handleMouseLeftClick, 'LEFT_CLICK'),
				this._subscriber.addExternal(this.handleMouseMove, 'MOUSE_MOVE'),
				this._subscriber.addExternal(this.handleMouseRightClick, 'RIGHT_CLICK'),
				this._subscriber.addExternal(this.endDraw, 'LEFT_DOUBLE_CLICK'),
			];
		}
		this.activeOnePosition(idx);
	};

	activeOnePosition = (index) => {
		const geo = this._pointGeometry[index];
		this._activePoint = geo;
		this.updatePointSize(geo, 8);
		this.updateLabel();
		this._viewer.scene.requestRender();
	};

	removeOnePosition = (index) => {
		if (this._positions.length <= 2) return false;
		const geo = this._pointGeometry[index];
		if (this._status === 'EDITING') this._subscriber.remove(geo, 'LEFT_CLICK');
		this._viewer.entities.remove(geo);
		this._positions.splice(index, 1);
		this._pointGeometry.splice(index, 1);

		this.updateMeasurements();
		this.handlePosChange();
		this._viewer.scene.requestRender();
		return true;
	};

	handleMouseRightClick = () => {
		if (!this._activePoint) return;
		const idx = this._pointGeometry.indexOf(this._activePoint);
		if (idx <= 0) return;
		this.removeOnePosition(idx - 1);
		this.updateLabel();
	};

	start(mode = 'polyline') {
		this.clear();

		this._mode = mode;
		this._pointGeometry = [];
		this._positions = [];

		if (this._mode !== 'height') {
			const initPos = new Cartesian3();
			const first = this.drawPoint(initPos);
			this._pointGeometry.push(first);
			this._activePoint = first;
		}

		this._subscriber.removeNative(this._viewer, 'LEFT_DOUBLE_CLICK');

		if (this._events.length === 0) {
			this._events = [
				this._subscriber.addExternal(this.handleMouseLeftClick, 'LEFT_CLICK'),
				this._subscriber.addExternal(this.handleMouseMove, 'MOUSE_MOVE'),
				this._subscriber.addExternal(this.handleMouseRightClick, 'RIGHT_CLICK'),
				this._subscriber.addExternal(this.endDraw, 'LEFT_DOUBLE_CLICK'),
			];
		}

		this.updateLabel();
		this._status = 'START';
		this.handlePosChange();
	}

	removeShape = () => {
		if (this._shape) {
			this._viewer.entities.remove(this._shape);
			this._shape = null;
		}
	};

	stop(removePoint = true) {
		if (removePoint) {
			this._subscriber.remove(this._pointGeometry, 'LEFT_CLICK');
			this._pointGeometry.forEach((ent) => this._viewer.entities.remove(ent));
			this._pointGeometry = [];
		}
		if (this._activePoint) {
			const idx = this._pointGeometry.indexOf(this._activePoint);
			this._viewer.entities.remove(this._activePoint);
			if (idx !== -1) {
				this._pointGeometry.splice(idx, 1);
				this._positions.splice(idx, 1);
			}
			this._activePoint = null;
		}

		this._measurementLabels.forEach((l) => this._viewer.entities.remove(l));
		this._measurementLabels = [];

		this._heightEntities.forEach((h) => this._viewer.entities.remove(h));
		this._heightEntities = [];

		this._status = 'END';
		this._lastClickPosition = new Cartesian2(Infinity, Infinity);
		this._viewer.scene.requestRender();
		return true;
	}

	cartesian2lonlat(cart) {
		if (!cart) return [];
		const accuracy = this._options.accuracy;
		const carto = this._viewer.scene.globe.ellipsoid.cartesianToCartographic(cart);
		const lon = +CesiumMath.toDegrees(carto.longitude).toFixed(accuracy);
		const lat = +CesiumMath.toDegrees(carto.latitude).toFixed(accuracy);
		const height = +carto.height.toFixed(2);
		return [lon, lat, height];
	}

	endDraw = () => {
		if (this._mode === 'height') {
			this._subscriber.removeExternal(this._events);
			this._events = [];
			this._status = 'END';
			this.updateLabel();
			return true;
		}

		if (!this._activePoint) return false;

		const idx = this._pointGeometry.indexOf(this._activePoint);
		this._positions.splice(idx, 1);
		this._viewer.entities.remove(this._activePoint);
		this._pointGeometry.splice(idx, 1);
		this._activePoint = null;

		if (this._mode === 'polygon' && this._positions.length < 3) return false;
		if (this._mode === 'polyline' && this._positions.length < 2) return false;

		if (!this._options.once) this.start(this._mode);
		else this.stop(false);

		if (!this._options.retainGeo) this.removeShape();

		this._subscriber.add(this._pointGeometry, this.onMouseClickPoint, 'LEFT_CLICK');
		if (this._callBack) {
			this._callBack(this._positions.map((cart) => this.cartesian2lonlat(cart)));
		}

		this.updateMeasurements();
		this.updateLabel();
		return true;
	};

	clear() {
		this.stop();
		this.removeShape();
		this.updateLabel();
	}

	destroy() {
		this._subscriber.removeExternal(this._events);
		this._events = [];
		this.clear();

		// Safely remove the cursor label entity we created on init
		if (this._cursorLabelEntity) {
			this._viewer.entities.remove(this._cursorLabelEntity);
			this._cursorLabelEntity = null;
		}

		this._subscriber.destroy();
		this._status = 'DESTROY';
	}
}
